import { useRecoilState } from 'recoil';

import { uploadFileV2 } from 'lib/api';
import { trackEvent } from 'lib/tracking';
import {
  showUploadErrorState,
  uploadErrorsState,
  uploadPendingState,
} from 'state/atoms/upload';
import { UploadCategory, UploadError, UploadResponse } from 'types/api';

const useUploadActions = () => {
  const [errors, setErrors] = useRecoilState(uploadErrorsState);
  const [pending, setPending] = useRecoilState(uploadPendingState);
  const [showError, setShowError] = useRecoilState(showUploadErrorState);

  const reset = () => {
    setPending(false);
    setErrors([]);
  };

  const uploadFiles = async (
    formSessionUuid: string,
    category: UploadCategory,
    files: File[]
  ): Promise<UploadResponse[]> => {
    setPending(true);

    const uploads = files.map((doc: File) =>
      uploadFileV2(doc, formSessionUuid, category)
    );
    const uploadResults = await Promise.allSettled(uploads);
    setPending(false);

    const uploadErrors = uploadResults.reduce(
      (accumulated: UploadError[], settledResult, index) => {
        if (settledResult.status === 'rejected') {
          const rejectedResult = settledResult as PromiseRejectedResult;
          const failedFile = files[index];
          trackEvent('request', 'upload', 'fail');
          return [
            ...accumulated,
            {
              fileName: failedFile.name,
              fileType: failedFile.type,
              error: rejectedResult.reason,
            },
          ];
        } else {
          trackEvent('request', 'upload', 'success');
          return accumulated;
        }
      },
      []
    );

    if (uploadErrors.length) {
      setErrors(uploadErrors);
      setShowError(true);
      throw new Error('Failed to upload files');
    }

    return uploadResults.map(
      (result) => (result as PromiseFulfilledResult<UploadResponse>).value
    );
  };

  return { errors, pending, reset, showError, uploadFiles };
};

export default useUploadActions;
